import { ReactElement } from 'react';
import { Card, CardContent, CardActions } from '@mui/material';
import { useAwsEffect, useAwsGetter, useRoutes } from '@localstack/services';
import { LambdaPutFunctionScalingConfigRequest } from '@localstack/types';
import { ProgressButton, LambdaFunctionScalingConfigForm, PageTitle } from '@localstack/ui';
import { useParams } from 'react-router-dom';

import { LambdaProps } from './types';
import { ScalingConfigAlert } from './components/ScalingConfigAlert';

export const LambdaFunctionScalingConfigUpdate = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const { name } = useParams<'name'>();
  const [functionNameWithoutVersion, functionVersion] = name?.split(':') ?? [];
  const isPublishedVersion = !!functionVersion && functionVersion !== '$LATEST';

  const { putFunctionScalingConfig, isLoading: isUpdating } = useAwsEffect('Lambda', ['putFunctionScalingConfig'], {
    revalidate: ['getFunctionScalingConfig'],
    clientOverrides,
  });

  const { data, isLoading: isFunctionScalingConfigLoading } = useAwsGetter(
    'Lambda',
    'getFunctionScalingConfig',
    [{ FunctionName: functionNameWithoutVersion, Qualifier: functionVersion }],
    { clientOverrides },
  );

  const handleUpdate = async (d: LambdaPutFunctionScalingConfigRequest) => {
    if (await putFunctionScalingConfig(d)) {
      goto(routes.RESOURCES_LAMBDA_FUNCTION_SCALING_CONFIG, { name });
    }
  };

  const isLoading = isUpdating || isFunctionScalingConfigLoading;

  return (
    <Layout
      documentTitle="Lambda: Update Event Function Scaling Config"
      title={
        <PageTitle
          title="Update Event Function Scaling Config"
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            [name, () => goto(routes.RESOURCES_LAMBDA_FUNCTION, { name })],
            ['Scaling Config', () => goto(routes.RESOURCES_LAMBDA_FUNCTION_SCALING_CONFIG, { name })],
            ['Update ', null],
          ]}
        />
      }
      actions={
        <ProgressButton
          type="submit"
          form="UpdateScalingConfigForm"
          variant="outlined"
          color="primary"
          loading={isLoading}
          disabled={!isPublishedVersion}
        >
          Submit
        </ProgressButton>
      }
    >
      {!isPublishedVersion && <ScalingConfigAlert />}
      <Card>
        <CardContent>
          <LambdaFunctionScalingConfigForm
            functionNameWithoutVersion={functionNameWithoutVersion}
            functionVersion={functionVersion}
            onSubmit={handleUpdate}
            scalingConfig={data?.RequestedFunctionScalingConfig}
            formId="UpdateScalingConfigForm"
            loading={isLoading}
            clientOverrides={clientOverrides}
          />
        </CardContent>
        <CardActions>
          <ProgressButton
            type="submit"
            form="UpdateScalingConfigForm"
            variant="contained"
            color="primary"
            loading={isLoading}
            disabled={!isPublishedVersion}
          >
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
